// Shared projectile + explosion system for EVERY combatant (tanks and soldiers fire into it).
// Handles straight bouncing shells/bullets/rockets, arcing lobs (grenades/mortars), homing
// missiles, and radial AoE blasts — with wall bounce (nav floor discontinuities), team-aware
// hit-testing across all registered CombatSides, and instanced rendering. Extracted from the tank
// bullet code so a bazooka soldier can kill a tank and a tank shell can kill a soldier, one system.

import * as THREE from "three";
import type { NavGraph } from "./pathfind";
import { sfx } from "../engine/sfx";

/** The shape a projectile can hit + damage (fighters and tanks satisfy it). */
export interface Combatant {
  team: 0 | 1;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  hp: number;
  dead: boolean;
  hitR: number;
  active?: boolean; // tanks toggle this; fighters leave it undefined (== always active)
}

/** How a hit was dealt — lets tanks/giants resist specific damage types. */
export type DamageType = "bullet" | "explosion" | "melee";

/** A team's units + how to damage one — registered with the projectile system for hit-testing. */
export interface CombatSide {
  units(): readonly Combatant[];
  damage(unit: Combatant, dmg: number, type: DamageType): void;
}

/** Concurrent-shell accounting owner (a tank); soldiers pass null and rely on their fire cooldown. */
export interface ProjectileOwner {
  shellsOut: number;
  gen: number;
}

const CAP = 1400;
const R_BASE = 5; // base shell render radius (studs); scaled per projectile
const TTL = 3.6;
const RENDER_Y = 14; // straight-shot height above the floor
const WALL_UP = 60;
const WALL_DOWN = 150;
const STEP = 10; // substep length (< smallest hit disc, anti-tunnel)
const ARC_TIME_PER = 380; // studs/sec of lob flight
const ARC_DUR_MIN = 0.9;
const ARC_DUR_MAX = 2.6;
const ARC_PEAK = 320; // default lob apex
const SEEK_TURN = 2.4;
const SEEK_TRIGGER = 30;
const EXPLO_CAP = 72;
const EXPLO_DUR = 0.32;
const EXPLO_COLOR = 0xff8a2a;

// color palette (per-instance): 0 blue, 1 red (team shots), 2 amber (arc), 3 orange (rocket)
const COLORS = [new THREE.Color(0x9fc4ff), new THREE.Color(0xffb0a0), new THREE.Color(0xffd27a), new THREE.Color(0xff7a2a)];

type Kind = "shell" | "arc" | "seek";

interface Proj {
  alive: boolean;
  kind: Kind;
  bx: number;
  bz: number;
  fy: number;
  ry: number;
  vx: number;
  vz: number;
  bounces: number;
  ttl: number;
  team: 0 | 1;
  damage: number;
  blastR: number; // >0 => explode (AoE) on impact instead of a single hit
  size: number; // render + hit radius scale
  colorId: number;
  owner: ProjectileOwner | null;
  gen: number;
  // arc:
  sx: number;
  sz: number;
  tx: number;
  tz: number;
  baseY: number;
  dur: number;
  t: number;
  peak: number;
  // seek:
  target: Combatant | null;
}

interface Explosion {
  alive: boolean;
  x: number;
  y: number;
  z: number;
  t: number;
  radius: number;
}

export class Projectiles {
  private group = new THREE.Group();
  private nav: NavGraph | null = null;
  private sides: CombatSide[] = [];
  private pool: Proj[] = [];
  private explosions: Explosion[] = [];
  private mesh: THREE.InstancedMesh;
  private exploMesh: THREE.InstancedMesh;
  private dummy = new THREE.Object3D();

  constructor(scene: THREE.Scene) {
    this.group.name = "projectiles";
    scene.add(this.group);

    const geo = new THREE.SphereGeometry(R_BASE, 8, 6);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.mesh = new THREE.InstancedMesh(geo, mat, CAP);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = CAP;
    for (let i = 0; i < CAP; i++) {
      this.pool.push(this.blankProj());
      this.hide(this.mesh, i);
      this.mesh.setColorAt(i, COLORS[0]);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.mesh.count = 0; // render() sets the live count each frame (compacted)
    this.group.add(this.mesh);

    const eGeo = new THREE.SphereGeometry(1, 10, 8);
    const eMat = new THREE.MeshBasicMaterial({ color: EXPLO_COLOR, transparent: true, opacity: 0.55 });
    this.exploMesh = new THREE.InstancedMesh(eGeo, eMat, EXPLO_CAP);
    this.exploMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.exploMesh.frustumCulled = false;
    this.exploMesh.count = EXPLO_CAP;
    for (let i = 0; i < EXPLO_CAP; i++) {
      this.explosions.push({ alive: false, x: 0, y: 0, z: 0, t: 0, radius: 0 });
      this.hide(this.exploMesh, i);
    }
    this.exploMesh.instanceMatrix.needsUpdate = true;
    this.exploMesh.count = 0;
    this.group.add(this.exploMesh);
  }

  private blankProj(): Proj {
    return {
      alive: false, kind: "shell", bx: 0, bz: 0, fy: 0, ry: 0, vx: 0, vz: 0, bounces: 0, ttl: 0,
      team: 0, damage: 0, blastR: 0, size: 1, colorId: 0, owner: null, gen: 0,
      sx: 0, sz: 0, tx: 0, tz: 0, baseY: 0, dur: 0, t: 0, peak: 0, target: null,
    };
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  addSide(side: CombatSide): void {
    this.sides.push(side);
  }

  /** climbMax: per-substep ground-rise that reads as a WALL. Tiles keep the default (WALL_UP=60,
   *  a discrete level face); terra passes ~20 — its fy re-tracks smooth ground every 10-stud substep,
   *  so walkable rolling slopes rise ≤~7/substep while crevasse/mesa faces rise 25-40 (which must
   *  bounce, not be climbed — review finding). */
  setNav(nav: NavGraph, climbMax = WALL_UP): void {
    this.nav = nav;
    this.climbMax = climbMax;
    for (const b of this.pool) b.alive = false;
    for (const e of this.explosions) e.alive = false;
  }

  private climbMax = WALL_UP;

  private hide(mesh: THREE.InstancedMesh, i: number): void {
    this.dummy.position.set(0, -100000, 0);
    this.dummy.scale.setScalar(0.0001);
    this.dummy.rotation.set(0, 0, 0);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(i, this.dummy.matrix);
  }

  private free(): Proj | null {
    for (const b of this.pool) if (!b.alive) return b;
    return null;
  }

  // ---- fire API ----

  /** A straight (optionally bouncing) shot: bullet / shell / rocket. blastR>0 => explodes on hit. */
  fireStraight(
    sx: number, sz: number, fy: number, dirx: number, dirz: number, speed: number,
    team: 0 | 1, damage: number, bounces: number, blastR: number, size: number, colorId: number,
    owner: ProjectileOwner | null, ttl = TTL,
  ): boolean {
    const b = this.free();
    if (!b) return false; // pool full — caller keeps its cooldown and retries next frame
    b.alive = true;
    b.kind = "shell";
    b.bx = sx;
    b.bz = sz;
    b.fy = fy;
    b.vx = dirx * speed;
    b.vz = dirz * speed;
    b.bounces = bounces;
    b.ttl = ttl;
    b.team = team;
    b.damage = damage;
    b.blastR = blastR;
    b.size = size;
    b.colorId = blastR > 0 ? 3 : colorId;
    b.owner = owner;
    b.gen = owner ? owner.gen : 0;
    b.target = null;
    if (owner) owner.shellsOut++;
    return true;
  }

  /** An arcing lob to (tx,tz): grenades + mortars. Always blasts (AoE) on landing. */
  fireArc(
    sx: number, sz: number, fy: number, tx: number, tz: number,
    team: 0 | 1, damage: number, blastR: number, peak: number, owner: ProjectileOwner | null,
  ): boolean {
    const b = this.free();
    if (!b) return false;
    const range = Math.hypot(tx - sx, tz - sz) || 1;
    b.alive = true;
    b.kind = "arc";
    b.sx = sx;
    b.sz = sz;
    b.bx = sx;
    b.bz = sz;
    b.fy = fy;
    b.tx = tx;
    b.tz = tz;
    b.baseY = this.nav!.heightAt(tx, tz) ?? fy;
    b.dur = Math.max(ARC_DUR_MIN, Math.min(ARC_DUR_MAX, range / ARC_TIME_PER));
    b.t = 0;
    b.peak = peak;
    b.team = team;
    b.damage = damage;
    b.blastR = blastR;
    b.size = 1.4;
    b.colorId = 2;
    b.owner = owner;
    b.gen = owner ? owner.gen : 0;
    if (owner) owner.shellsOut++;
    return true;
  }

  /** A homing missile toward `target`; blasts on proximity. */
  fireSeek(
    sx: number, sz: number, fy: number, dirx: number, dirz: number, speed: number,
    team: 0 | 1, damage: number, blastR: number, target: Combatant, owner: ProjectileOwner | null,
  ): boolean {
    const b = this.free();
    if (!b) return false;
    b.alive = true;
    b.kind = "seek";
    b.bx = sx;
    b.bz = sz;
    b.fy = fy;
    b.vx = dirx * speed;
    b.vz = dirz * speed;
    b.bounces = 0;
    b.ttl = TTL * 1.5;
    b.team = team;
    b.damage = damage;
    b.blastR = blastR;
    b.size = 1.2;
    b.colorId = 3;
    b.owner = owner;
    b.gen = owner ? owner.gen : 0;
    b.target = target;
    if (owner) owner.shellsOut++;
    return true;
  }

  // Peak passthrough for callers.
  static readonly ARC_PEAK = ARC_PEAK;

  // ---- physics ----

  private kill(b: Proj): void {
    if (!b.alive) return;
    b.alive = false;
    if (b.owner && b.owner.gen === b.gen && b.owner.shellsOut > 0) b.owner.shellsOut--;
  }

  private blocked(nx: number, nz: number, fy: number): boolean {
    const h = this.nav!.heightAt(nx, nz);
    return h == null || h - fy > this.climbMax || fy - h > WALL_DOWN;
  }

  /** Single-target hit: damage the first enemy the point overlaps (across all sides). */
  private hitEnemy(b: Proj): boolean {
    const rExtra = R_BASE * b.size;
    for (const side of this.sides) {
      for (const u of side.units()) {
        if (u.dead || u.active === false || u.team === b.team) continue;
        if ((u.pos.x - b.bx) ** 2 + (u.pos.z - b.bz) ** 2 < (u.hitR + rExtra) ** 2) {
          side.damage(u, b.damage, "bullet");
          return true;
        }
      }
    }
    return false;
  }

  /** Is any enemy within `extra`+hitR (proximity fuse for homing)? */
  private nearEnemy(b: Proj, extra: number): boolean {
    for (const side of this.sides) {
      for (const u of side.units()) {
        if (u.dead || u.active === false || u.team === b.team) continue;
        if ((u.pos.x - b.bx) ** 2 + (u.pos.z - b.bz) ** 2 < (u.hitR + extra) ** 2) return true;
      }
    }
    return false;
  }

  /** Radial blast: damage every enemy within `radius`, plus a visual + sound. */
  explode(x: number, z: number, y: number, team: 0 | 1, radius: number, damage: number): void {
    sfx.play("explode", { x, y, z });
    for (const side of this.sides) {
      for (const u of side.units()) {
        if (u.dead || u.active === false || u.team === team) continue;
        if ((u.pos.x - x) ** 2 + (u.pos.z - z) ** 2 < (radius + u.hitR) ** 2) side.damage(u, damage, "explosion");
      }
    }
    let e = this.explosions.find((x2) => !x2.alive);
    if (!e) {
      const oldest = this.explosions.reduce((a, b) => (b.t > a.t ? b : a));
      if (oldest.t <= 0) return; // all live blasts spawned THIS frame — drop this VFX, don't stomp one
      e = oldest;
    }
    e.alive = true;
    e.x = x;
    e.y = y + 20;
    e.z = z;
    e.t = 0;
    e.radius = radius;
  }

  private updateBullets(dt: number): void {
    const nav = this.nav!;
    for (const b of this.pool) {
      if (!b.alive) continue;

      if (b.kind === "arc") {
        b.t += dt;
        const f = Math.min(1, b.t / b.dur);
        b.bx = b.sx + (b.tx - b.sx) * f;
        b.bz = b.sz + (b.tz - b.sz) * f;
        b.ry = b.fy + (b.baseY - b.fy) * f + b.peak * 4 * f * (1 - f);
        if (f >= 1) {
          this.explode(b.tx, b.tz, b.baseY, b.team, b.blastR, b.damage);
          this.kill(b);
        }
        continue;
      }

      b.ttl -= dt;
      if (b.ttl <= 0) {
        this.kill(b);
        continue;
      }

      if (b.kind === "seek") {
        const tg = b.target;
        if (tg && !tg.dead) {
          const spd = Math.hypot(b.vx, b.vz) || 1;
          const want = Math.atan2(tg.pos.x - b.bx, tg.pos.z - b.bz);
          const cur = Math.atan2(b.vx, b.vz);
          let d = want - cur;
          while (d > Math.PI) d -= Math.PI * 2;
          while (d < -Math.PI) d += Math.PI * 2;
          const na = cur + Math.max(-SEEK_TURN * dt, Math.min(SEEK_TURN * dt, d));
          b.vx = Math.sin(na) * spd;
          b.vz = Math.cos(na) * spd;
        }
        if (this.nearEnemy(b, SEEK_TRIGGER)) {
          this.explode(b.bx, b.bz, b.fy, b.team, b.blastR, b.damage);
          this.kill(b);
          continue;
        }
      }

      const speed = Math.hypot(b.vx, b.vz);
      const steps = Math.max(1, Math.ceil((speed * dt) / STEP));
      const sdt = dt / steps;
      let dead = false;
      for (let s = 0; s < steps; s++) {
        const nx = b.bx + b.vx * sdt;
        const nz = b.bz + b.vz * sdt;
        if (this.blocked(nx, nz, b.fy)) {
          const bX = this.blocked(nx, b.bz, b.fy);
          const bZ = this.blocked(b.bx, nz, b.fy);
          if (bX) b.vx = -b.vx;
          if (bZ) b.vz = -b.vz;
          if (!bX && !bZ) {
            b.vx = -b.vx;
            b.vz = -b.vz;
          }
          b.bounces--;
          if (b.bounces < 0) {
            if (b.blastR > 0) this.explode(b.bx, b.bz, b.fy, b.team, b.blastR, b.damage);
            this.kill(b);
            dead = true;
            break;
          }
          sfx.play("bounce", { x: b.bx, y: b.fy, z: b.bz });
          continue;
        }
        b.bx = nx;
        b.bz = nz;
        const h = nav.heightAt(nx, nz);
        if (h != null) b.fy = h;
        if (b.blastR === 0) {
          if (this.hitEnemy(b)) {
            this.kill(b);
            dead = true;
            break;
          }
        } else if (this.nearEnemy(b, R_BASE * b.size)) {
          this.explode(b.bx, b.bz, b.fy, b.team, b.blastR, b.damage);
          this.kill(b);
          dead = true;
          break;
        }
      }
      if (dead) continue;
      b.ry = b.fy + RENDER_Y;
    }
  }

  private render(): void {
    // COMPACT the alive shells to the front of the instance buffer and draw only that range
    // (mesh.count=n). Dead instances beyond n cost nothing — no per-frame hide() of ~1400 slots,
    // and the vertex stage no longer processes ~112k tris of hidden spheres every idle frame.
    let n = 0;
    for (const b of this.pool) {
      if (!b.alive) continue;
      this.dummy.position.set(b.bx, b.ry, b.bz);
      this.dummy.scale.setScalar(b.size);
      // rotation stays identity (never set non-zero) → skip the per-instance setFromEuler
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(n, this.dummy.matrix);
      this.mesh.setColorAt(n, COLORS[b.colorId]);
      n++;
    }
    this.mesh.count = n;
    this.mesh.visible = n > 0; // count 0 still pays a bind + draw call in all 4 passes; hiding rasterizes the same nothing
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    }

    let en = 0;
    for (const e of this.explosions) {
      if (!e.alive) continue;
      const p = e.t / EXPLO_DUR;
      const s = (0.35 + 0.65 * p) * (p < 0.6 ? 1 : 1 - (p - 0.6) / 0.4);
      this.dummy.position.set(e.x, e.y, e.z);
      this.dummy.scale.setScalar(e.radius * Math.max(0.001, s));
      // rotation stays identity → skip the per-instance setFromEuler
      this.dummy.updateMatrix();
      this.exploMesh.setMatrixAt(en, this.dummy.matrix);
      en++;
    }
    this.exploMesh.count = en;
    this.exploMesh.visible = en > 0; // explosions are empty most of the time — this one is almost always hidden
    if (en > 0) this.exploMesh.instanceMatrix.needsUpdate = true;
  }

  update(dt: number): void {
    if (!this.nav) return;
    if (dt < 1e-4) return;
    if (dt > 1 / 30) dt = 1 / 30;
    this.updateBullets(dt);
    for (const e of this.explosions) {
      if (!e.alive) continue;
      e.t += dt;
      if (e.t >= EXPLO_DUR) e.alive = false;
    }
    this.render();
  }
}
